import { defineStore } from 'pinia';
import { JWTHttpClient } from '@/services/JWTHttpClient';
import type { CopilotSearchResult } from '@/types/copilot';
import type { UserContext } from '@/composables/useUserContext';

// Create JWT HTTP client for CoPilot API
const copilotJWTClient = new JWTHttpClient(import.meta.env.VITE_COPILOT_API_HOST || 'https://genai.ovationcxm.ai');

export const useCopilotStore = defineStore('copilot', {
    state: () => {
        return {
            searchUrl: 'https://genai.ovationcxm.ai/agents/run/stream',
            streamUrl: 'https://genai.ovationcxm.ai/agents/run/stream'
        };
    },
    getters: {},
    actions: {
        setCopilotUrls(urls: { searchUrl: string; streamUrl: string }) {
            this.searchUrl = urls.searchUrl;
            this.streamUrl = urls.streamUrl;
        },
        
        async submitSearch(
            searchData: string | { 
                query: string; 
                context?: any; 
                session_id?: string; 
                userContext?: UserContext
                // Deprecated: user_id (use userContext instead)
                user_id?: string;
            }, 
            onContentUpdate?: (content: string) => void
        ): Promise<CopilotSearchResult> {
            try {
                // Handle both string and structured data formats
                const data = typeof searchData === 'string' 
                    ? { query: searchData }
                    : { 
                        query: searchData.query,
                        ...(searchData.context && { context: searchData.context }),
                        ...(searchData.session_id && { session_id: searchData.session_id }),
                        ...(searchData.userContext && { userContext: searchData.userContext }),
                        ...(searchData.user_id && { user_id: searchData.user_id })
                      };
                
                const url = this.streamUrl;
                
                // Use JWT HTTP client for streaming request
                const response = await copilotJWTClient.postStream(
                    url.replace(copilotJWTClient['baseUrl'] || import.meta.env.VITE_COPILOT_API_HOST || 'https://genai.ovationcxm.ai', ''),
                    data
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body?.getReader();
                if (!reader) {
                    throw new Error('Failed to get response reader');
                }

                const decoder = new TextDecoder();
                let content = '';
                let metadata: any = null;
                let traceId: string | null = null;
                let returnedSessionId: string | null = null;
                let buffer = '';
                
                try {
                    while (true) {
                        const { done, value } = await reader.read();
                        
                        if (done) break;
                        
                        const chunk = decoder.decode(value, { stream: true });
                        buffer += chunk;
                        
                        // Split by lines and process each complete line
                        const lines = buffer.split('\n');
                        // Keep the last potentially incomplete line in buffer
                        buffer = lines.pop() || '';
                        
                        for (const line of lines) {
                            const trimmedLine = line.trim();
                            if (!trimmedLine) continue;
                            
                            try {
                                // Try parsing as direct JSON first
                                let parsed;
                                if (trimmedLine.startsWith('data: ')) {
                                    // Server-Sent Events format
                                    const jsonData = trimmedLine.slice(6);
                                    if (jsonData.trim() === '[DONE]') break;
                                    parsed = JSON.parse(jsonData);
                                } else {
                                    // Direct JSON format
                                    parsed = JSON.parse(trimmedLine);
                                }
                                
                                if (parsed.type === 'content') {
                                    content += parsed.content || '';
                                    // Call the callback with the updated content for real-time streaming
                                    if (onContentUpdate) {
                                        onContentUpdate(content);
                                    }
                                } else if (parsed.type === 'trace') {
                                    traceId = parsed.trace_id || null;
                                } else if (parsed.type === 'metadata') {
                                    metadata = parsed;
                                } else if (parsed.type === 'done') {
                                    returnedSessionId = parsed.session_id || null;
                                    break;
                                }
                            } catch (parseError) {
                                console.warn('Failed to parse line:', trimmedLine, parseError);
                            }
                        }
                    }
                    
                    // Process any remaining buffer
                    if (buffer.trim()) {
                        try {
                            const parsed = JSON.parse(buffer.trim());
                            if (parsed.type === 'content') {
                                content += parsed.content || '';
                                if (onContentUpdate) {
                                    onContentUpdate(content);
                                }
                            } else if (parsed.type === 'trace') {
                                traceId = parsed.trace_id || null;
                            } else if (parsed.type === 'metadata') {
                                metadata = parsed;
                            } else if (parsed.type === 'done') {
                                returnedSessionId = parsed.session_id || null;
                            }
                        } catch (parseError) {
                            console.warn('Failed to parse final buffer:', buffer, parseError);
                        }
                    }
                } finally {
                    reader.releaseLock();
                }

                // Use content if available, otherwise fall back to complete_message from metadata
                const finalMessage = content || metadata?.complete_message || '';

                return {
                    message: finalMessage,
                    traceId: traceId,
                    sessionId: returnedSessionId,
                    sources: metadata?.source ? metadata.source.map((s: any) => {
                        // Handle both string URLs and object sources
                        if (typeof s === 'string') {
                            return { url: s };
                        }
                        // Return the full source object with all properties
                        return {
                            url: s.url,
                            filename: s.filename,
                            title: s.title,
                            file_id: s.file_id,
                            type: s.type,
                            is_downloadable: s.is_downloadable
                        };
                    }) : [],
                    relatedLinks: metadata?.relatedLinks ? metadata.relatedLinks.map((r: any) => {
                        // Handle both string URLs and object links
                        if (typeof r === 'string') {
                            return { url: r };
                        }
                        return {
                            url: r.url,
                            title: r.title || r.filename
                        };
                    }) : [],
                    triggers: metadata?.triggers || { esscalcateCase: false },
                };
            } catch (error) {
                console.error('Failed to submit search:', error);
                throw error;
            }
        },
    },
}); 